import { Search, ShoppingBag, Store } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { MockupTabBar } from '@/pages/onboarding/mockup/mockup-tab-bar';

export interface StoreAppSceneProps {
  storeName: string;
  iconUrl?: string | null;
  /** Under the status bar (banner at the top). */
  top?: React.ReactNode;
  /** Above the tab bar (banner at the bottom). */
  bottom?: React.ReactNode;
}

const MOCK_TABS = [
  { key: 'home', title: 'Início', path: '/', iconKey: 'home' },
  { key: 'search', title: 'Buscar', path: '/search', iconKey: 'search' },
  { key: 'cart', title: 'Carrinho', path: '/cart', iconKey: 'cart' },
  { key: 'user', title: 'Perfil', path: '/account', iconKey: 'user' },
];

const TAB_ACCENT = '#111111';
const TILE = 'radial-gradient(80% 60% at 50% 25%, #F4F5F7, #E1E4E8)';
const HERO =
  'radial-gradient(70% 90% at 78% 25%, #F1F2F4 0%, transparent 70%), linear-gradient(135deg, #DDE0E5 0%, #C6CBD2 100%)';

function ProductTile({ nameWidth }: { nameWidth: string }) {
  return (
    <div className="flex min-h-0 flex-col gap-[5px] pb-2">
      <div
        className="min-h-0 flex-1 rounded-[10px]"
        style={{ background: TILE }}
      />
      <span
        className="mt-[3px] h-2 rounded bg-[#E1E4E8]"
        style={{ width: nameWidth }}
      />
      <span className="h-[9px] w-[44%] rounded-[5px] bg-[#C9CED6]" />
    </div>
  );
}

/**
 * The store's app open, drawn for IPhoneMockup's 300px screen (its status
 * bar and home indicator are the mockup's). Only the store's real data shows
 * (icon and name, from the tenant): the shop window is neutral, the same for
 * every store, so the coupon banner is what stands out. The banner slots are
 * in the flow, so the banner really moves between top and bottom.
 */
export function StoreAppScene({
  storeName,
  iconUrl,
  top,
  bottom,
}: StoreAppSceneProps) {
  const { LL } = useI18nContext();

  return (
    <div className="flex h-full w-full flex-col bg-white text-[#111111]">
      {/* Safe area under the status bar and the Dynamic Island. */}
      <div className="h-[46px] shrink-0" />
      {top && <div data-store-slot="top">{top}</div>}
      <div className="flex min-h-[46px] shrink-0 items-center gap-[9px] px-3.5 py-1.5">
        {iconUrl ? (
          <img
            src={iconUrl}
            alt=""
            className="size-7 shrink-0 rounded-[7px] object-cover"
          />
        ) : (
          <span className="flex size-7 shrink-0 items-center justify-center rounded-[7px] bg-[#F2F2F4]">
            <Store aria-hidden className="size-4 text-[#6E6E73]" />
          </span>
        )}
        <span className="line-clamp-2 min-w-0 flex-1 text-[15px] font-semibold leading-[1.2] tracking-[-0.01em]">
          {storeName}
        </span>
        <ShoppingBag
          aria-hidden
          strokeWidth={1.6}
          className="size-5 shrink-0"
        />
      </div>
      <div className="mx-3.5 mb-3 mt-0.5 flex h-[34px] shrink-0 items-center gap-2 rounded-[10px] bg-[#F2F2F4] px-[11px] text-[13px] text-[#6E6E73]">
        <Search aria-hidden strokeWidth={2} className="size-3.5 shrink-0" />
        {LL.couponEditor.storeApp.search()}
      </div>
      <div
        className="relative mx-3.5 h-32 shrink-0 overflow-hidden rounded-[14px]"
        style={{ background: HERO }}
      >
        <div className="absolute bottom-4 left-3.5 flex flex-col gap-[7px]">
          <span className="h-1.5 w-[54px] rounded-[3px] bg-white/75" />
          <span className="h-3 w-[108px] rounded-md bg-white" />
          <span className="h-3 w-[76px] rounded-md bg-white" />
        </div>
      </div>
      <div className="flex shrink-0 gap-1.5 px-3.5 pb-1 pt-3">
        <span className="h-[26px] w-[70px] rounded-full bg-[#111111]" />
        <span className="h-[26px] w-[58px] rounded-full bg-[#F2F2F4]" />
        <span className="h-[26px] w-16 rounded-full bg-[#F2F2F4]" />
        <span className="h-[26px] w-11 rounded-full bg-[#F2F2F4]" />
      </div>
      <div className="flex shrink-0 items-center justify-between px-3.5 pb-2.5 pt-3.5">
        <span className="h-2.5 w-24 rounded-[5px] bg-[#D9DCE1]" />
        <span className="h-[7px] w-11 rounded bg-[#E6E8EB]" />
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-2 gap-2.5 overflow-hidden px-3.5">
        <ProductTile nameWidth="82%" />
        <ProductTile nameWidth="70%" />
      </div>
      {bottom && <div data-store-slot="bottom">{bottom}</div>}
      <MockupTabBar tabs={MOCK_TABS} accentColor={TAB_ACCENT} />
    </div>
  );
}
